import React, {useEffect, useMemo} from 'react';
import {Form} from 'react-final-form';
import {useIntl} from 'react-intl';
import cn from 'classnames';

import ProductItemModel from 'coreClasses/basket/ProductItemModel';
import {ReturnOrderProductData} from 'coreClasses/returnAuthorization/IReturnOrderData';

import {useGeneralProvider} from 'brandComponents/_app-config/GeneralProvider';
import Button from 'brandComponents/Button';
import Money from 'brandComponents/Money';
import Typography from 'brandComponents/Typography';
import VerifyForm from 'brandComponents/VerifyForm';

import {ReturnStatusBar} from '../index';

import {IStep2Props} from './IStep2';
import messages from './messages';
import {ExchangeProducts, PaymentInstrumentContent, Step2Products} from './Subcomponents';

import returnCreateFormStyles from '../../ReturnAuthorizationCreateForm.module.scss';
import styles from './Step2.module.scss';

const Step2 = ({
	handleStep,
	setStep,
	returnOrder,
	returnOrderData,
	returnReasons,
	exchangeReasons,
	goToStep,
	step
}: IStep2Props) => {
	const intl = useIntl();
	const {managerRepository} = useGeneralProvider();

	useEffect(() =>{
		managerRepository.getUiMgr().setColorSelectorSelectedPid(null);
		managerRepository.getUiMgr().setSizeSelectorSelectedPid(null);
	}, [goToStep]);

	const productsToReturn: ProductItemModel[] = useMemo(() => {
		let products = [];
		returnOrderData.getProducts().forEach((product: ReturnOrderProductData) => {
			products.push(returnOrder.getProductItem(product.lineItemID));
		});

		return products;
	}, [returnOrderData]);

	const exchangeProducts: ProductItemModel[] = useMemo(() => {
		return returnOrderData.getProducts()
			.filter(product => !!product.newItemID);
	}, [returnOrderData]);

	const productsToRefund: ProductItemModel[] = useMemo(() => {
		let products = [];

		returnOrderData.getProducts()
			.filter((product: ReturnOrderProductData) => {
				if (product.newItemID === undefined) {
					products.push(returnOrder.getProductItem(product.lineItemID));
				}
			});

		return products;
	}, [returnOrderData]);

	const amountToRefund: number = useMemo(() => {
		const initialValue = 0;

		return productsToRefund.reduce(
			(accumulator, currentValue) =>
				accumulator +
				(
					(currentValue.isExchanged()
						? currentValue.getOriginalExchangedPrice()
						:
						(
							currentValue.getProratedPriceAdjustmentPrices() ??
							currentValue.getPriceAfterItemDiscount() ??
							currentValue.getPrice()
						)
					) / currentValue.getQuantity()
				),
			initialValue,
		);
	}, [returnOrder]);

	return (
		<>
			<Button
				onClick={() => goToStep(1)}
				variant="tertiary"
				classNames={returnCreateFormStyles['return-create__form-back-btn']}
			>
				{intl.formatMessage(messages.backToProductSelection)}
			</Button>
			<Typography variant="h3" classNames={styles['return-create-step-2__title']}>
				{intl.formatMessage(messages.title)}
			</Typography>
			<ReturnStatusBar currentStep={2} />
			<Form
				onSubmit={(values) => handleStep(values)}
				render={({handleSubmit, submitting}) => (
					<VerifyForm onSubmit={handleSubmit}>
						<Step2Products
							returnOrder={returnOrder}
							returnOrderData={returnOrderData}
							returnReasons={returnReasons}
							exchangeReasons={exchangeReasons}
							goToStep={goToStep}
							step={step}
						/>
						<PaymentInstrumentContent
							setStep={setStep}
							returnOrder={returnOrder}
							returnOrderData={returnOrderData}
						/>
						{
							exchangeProducts.length > 0 && (
								<ExchangeProducts
									exchangeProducts={exchangeProducts}
									step={step}
									returnOrder={returnOrder}
								/>
							)
						}

						<div className={styles['return-create-step-2__total']}>
							<div>
								{intl.formatMessage(messages.productsToReturnNumber, {
									products: productsToReturn.length,
								})}
							</div>
							<div className={styles['return-create-step-2__total--detail']}>
								<p>
									{intl.formatMessage(messages.productsToExchangeNumber, {
										products: exchangeProducts.length,
									})}
								</p>
								<p>
									{intl.formatMessage(messages.productsToRefundNumber, {
										products: productsToRefund.length,
									})}
								</p>
							</div>
							<div>
								{intl.formatMessage(messages.amountToRefund)}
								<Money value={amountToRefund} currency={returnOrder.getCurrency()} spaceBetweenCurrencyAndPrice={false} />
							</div>
						</div>
						<div className={returnCreateFormStyles['return-create__form-apply-wrapper']}>
							<Button
								disabled={submitting}
								classNames={cn(
									returnCreateFormStyles['return-create__form-apply'],
									styles['return-create-step-2__apply-btn'],
								)}
								type="submit"
							>
								{intl.formatMessage(messages.createButtonText)}
							</Button>
						</div>
					</VerifyForm>
				)}
			/>
		</>
	);
};

export default Step2;
